import React, { useState } from 'react'
import { Box, Tab, Tabs, Typography } from '@mui/material'
import ThemeDistribution, { ThemeDistributionItem } from './ThemeDistribution'
import { emotionColour, sentimentColour } from '../../utils/themeAISentimentColours'

export type ThemeAILoopedKind = 'sentiment' | 'emotion'

export interface ThemeAILoopedFilter {
  kind: ThemeAILoopedKind
  code: string
}

type FilterTab = 'themes' | ThemeAILoopedKind

interface ResultsFilterPanelProps {
  themes: ThemeDistributionItem[]
  sentiment: ThemeDistributionItem[]
  emotion: ThemeDistributionItem[]
  totalResponses: number
  themeColour: (code: string | undefined) => string | undefined
  selectedThemeCode?: string
  selectedLooped?: ThemeAILoopedFilter
  onSelectTheme: (code: string | undefined) => void
  onSelectLooped: (filter: ThemeAILoopedFilter | undefined) => void
}

const TAB_LABELS: Record<FilterTab, string> = { themes: 'Themes', sentiment: 'Sentiment', emotion: 'Emotion' }

// The filter box on the left of Results: the themes, and the sentiment and
// emotion when the run coded them, as tabs in one place (Adam, 24 Sep: "you
// click them, and it will just swap backwards and forwards between them").
// Clicking a bar in any tab filters the responses beside it. The page keeps it
// in view while the responses scroll, so a filter is always one click away.
const ResultsFilterPanel: React.FC<ResultsFilterPanelProps> = ({
  themes,
  sentiment,
  emotion,
  totalResponses,
  themeColour,
  selectedThemeCode,
  selectedLooped,
  onSelectTheme,
  onSelectLooped,
}) => {
  const tabs: FilterTab[] = ['themes', ...(sentiment.length > 0 ? ['sentiment' as const] : []), ...(emotion.length > 0 ? ['emotion' as const] : [])]
  const [chosen, setChosen] = useState<FilterTab>(selectedLooped?.kind ?? 'themes')
  const tab = tabs.includes(chosen) ? chosen : 'themes'

  const handleLooped = (kind: ThemeAILoopedKind) => (code: string | undefined) =>
    onSelectLooped(code === undefined ? undefined : { kind, code })

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        // Fill the column: as a flex item it would shrink to its widest label,
        // so it changed width between tabs (Adam, 24 Sep).
        flex: 1,
        minWidth: 0,
      }}
    >
      {tabs.length > 1 && (
        <Tabs
          value={tab}
          onChange={(_, value: FilterTab) => setChosen(value)}
          textColor="inherit"
          indicatorColor="primary"
          variant="fullWidth"
          aria-label="Filter the responses by"
          sx={{ borderBottom: '1px solid', borderColor: 'divider', minHeight: 40, '& .MuiTab-root': { minHeight: 40 } }}
        >
          {tabs.map((key) => <Tab key={key} value={key} label={TAB_LABELS[key]} />)}
        </Tabs>
      )}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1, overflowY: 'auto', minHeight: 0 }}>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ px: 1, mb: 1 }}>
          Click a bar to filter the responses.
        </Typography>
        {tab === 'themes' && (
          <ThemeDistribution
            bare
            items={themes}
            totalResponses={totalResponses}
            selectedCode={selectedThemeCode}
            onSelectTheme={onSelectTheme}
            colorFor={(item) => themeColour(item.code)}
          />
        )}
        {tab === 'sentiment' && (
          <ThemeDistribution
            bare
            title="Sentiment"
            items={sentiment}
            totalResponses={totalResponses}
            selectedCode={selectedLooped?.kind === 'sentiment' ? selectedLooped.code : undefined}
            onSelectTheme={handleLooped('sentiment')}
            colorFor={(item) => sentimentColour(item.name)}
          />
        )}
        {tab === 'emotion' && (
          <ThemeDistribution
            bare
            title="Emotion"
            items={emotion}
            totalResponses={totalResponses}
            selectedCode={selectedLooped?.kind === 'emotion' ? selectedLooped.code : undefined}
            onSelectTheme={handleLooped('emotion')}
            colorFor={(item) => emotionColour(item.name)}
          />
        )}
      </Box>
    </Box>
  )
}

export default ResultsFilterPanel
